import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

type ErrorBoundaryLocale = "ru" | "uk" | "en";

interface ErrorBoundaryCopy {
	title: string;
	body: string;
	reload: string;
}

// Kept as a fallback beside the dictionary keys: this screen renders precisely when something
// upstream is broken, and that can include i18n never having initialised.
const COPY: Record<ErrorBoundaryLocale, ErrorBoundaryCopy> = {
	ru: {
		title: "Что-то пошло не так",
		body: "Не удалось загрузить эту часть приложения. Перезагрузите страницу и попробуйте снова.",
		reload: "Перезагрузить страницу",
	},
	uk: {
		title: "Щось пішло не так",
		body: "Не вдалося завантажити цю частину застосунку. Перезавантажте сторінку та спробуйте ще раз.",
		reload: "Перезавантажити сторінку",
	},
	en: {
		title: "Something went wrong",
		body: "This part of the app could not be loaded. Reload the page and try again.",
		reload: "Reload page",
	},
};

const copyFor = (language: string): ErrorBoundaryCopy => {
	const short = language.toLowerCase().slice(0, 2);
	const locale: ErrorBoundaryLocale = short === "ru" || short === "en" ? short : "uk";
	return COPY[locale];
};

const ErrorFallback = () => {
	const { t, i18n } = useTranslation();
	const copy = copyFor(i18n.resolvedLanguage || i18n.language);

	return (
		<div
			role="alert"
			className="flex h-full min-h-[240px] w-full flex-col items-center justify-center bg-bg-page px-[24px] text-center dark:bg-bg-page"
		>
			<h1 className="text-[20px] font-semibold text-text-primary dark:text-text-dark">
				{t("common-components.error-boundary.title", copy.title)}
			</h1>
			<p className="mt-[8px] max-w-[460px] text-[14px] leading-[1.55] text-text-secondary dark:text-text-dark-muted">
				{t("common-components.error-boundary.body", copy.body)}
			</p>
			<button
				type="button"
				className="mt-[18px] rounded-[8px] bg-primary px-[16px] py-[9px] text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
				onClick={() => window.location.reload()}
			>
				{t("common-components.error-boundary.reload", copy.reload)}
			</button>
		</div>
	);
};

interface ErrorBoundaryProps {
	children: ReactNode;
	resetKey?: unknown;
}

interface ErrorBoundaryState {
	hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	state: ErrorBoundaryState = { hasError: false };

	static getDerivedStateFromError(): ErrorBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		console.error("[error-boundary] Uncaught render error", error, info);
	}

	componentDidUpdate(previousProps: ErrorBoundaryProps) {
		if (this.state.hasError && previousProps.resetKey !== this.props.resetKey) {
			this.setState({ hasError: false });
		}
	}

	render() {
		if (this.state.hasError) return <ErrorFallback />;
		return this.props.children;
	}
}

export default ErrorBoundary;
